// Gmail API Service for TherapyLink CRM
// Handles sending, drafts, reading threads, and searching emails
// Requires googleAuth.js for token management

import { getAccessToken, isSignedIn } from './googleAuth';
import { encodeRawMessage } from './lib/mimeEncode';

const GMAIL_BASE = 'https://gmail.googleapis.com/gmail/v1/users/me';

// ============================================================
// SENDING EMAILS
// ============================================================

/**
 * Send an email directly via Gmail API
 * @param {string} to - Recipient email
 * @param {string} subject - Email subject
 * @param {string} body - Plain text body
 * @param {string} [replyToMessageId] - Message-ID header for threading replies
 * @returns {Promise<{id: string, threadId: string, labelIds: string[]}>}
 */
export async function sendEmail(to, subject, body, replyToMessageId = null) {
  const token = await getAccessToken();

  const extraHeaders = [];
  if (replyToMessageId) {
    extraHeaders.push(`In-Reply-To: ${replyToMessageId}`);
    extraHeaders.push(`References: ${replyToMessageId}`);
  }

  const encoded = encodeRawMessage({ to, subject, body, extraHeaders });

  const response = await fetch(`${GMAIL_BASE}/messages/send`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ raw: encoded }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gmail send failed (${response.status})`);
  }

  return response.json();
}

/**
 * Send bulk emails — sends individually to each recipient with merge fields
 * @param {Array<{email: string, name: string, specialty?: string, state?: string, id?: string}>} recipients
 * @param {string} subject - Subject with optional {name}, {specialty}, {state} merge fields
 * @param {string} body - Body with optional merge fields
 * @param {function} [onProgress] - Callback(index, total, recipientName, success)
 * @returns {Promise<{sent: number, failed: number, results: Array}>}
 */
export async function sendBulkEmails(recipients, subject, body, onProgress) {
  const results = [];
  let sent = 0;
  let failed = 0;

  for (let i = 0; i < recipients.length; i++) {
    const r = recipients[i];
    const firstName = r.name?.split(' ')[0] || '';
    const mergedSubject = subject
      .replace(/{name}/g, firstName)
      .replace(/{specialty}/g, r.specialty || '')
      .replace(/{state}/g, r.state || '');
    const mergedBody = body
      .replace(/{name}/g, firstName)
      .replace(/{specialty}/g, r.specialty || '')
      .replace(/{state}/g, r.state || '');

    try {
      const result = await sendEmail(r.email, mergedSubject, mergedBody);
      sent++;
      results.push({ ...r, success: true, messageId: result.id, threadId: result.threadId });
      onProgress?.(i + 1, recipients.length, firstName, true);
    } catch (err) {
      failed++;
      results.push({ ...r, success: false, error: err.message });
      onProgress?.(i + 1, recipients.length, firstName, false);
    }

    // Small delay between sends to avoid rate limiting
    if (i < recipients.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  return { sent, failed, results };
}


// ============================================================
// DRAFTS
// ============================================================

/**
 * Create a Gmail draft
 * @param {string} to - Recipient email
 * @param {string} subject - Email subject
 * @param {string} body - Plain text body
 * @returns {Promise<{id: string, message: {id: string, threadId: string}}>}
 */
export async function createDraft(to, subject, body) {
  const token = await getAccessToken();

  const encoded = encodeRawMessage({ to, subject, body });

  const response = await fetch(`${GMAIL_BASE}/drafts`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message: { raw: encoded } }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Draft creation failed (${response.status})`);
  }

  return response.json();
}


// ============================================================
// READING & SEARCHING EMAILS
// ============================================================

/**
 * Search Gmail for emails to/from a specific email address
 * Returns parsed email objects sorted newest first
 * @param {string} email - Contact email to search for
 * @param {number} [maxResults=20] - Max emails to return
 * @returns {Promise<Array<{id, threadId, date, from, to, subject, snippet, body, direction}>>}
 */
export async function getEmailsForContact(email, maxResults = 20) {
  const token = await getAccessToken();
  
  // Search for emails to or from this contact
  const query = encodeURIComponent(`from:${email} OR to:${email}`);
  const listResponse = await fetch(
    `${GMAIL_BASE}/messages?q=${query}&maxResults=${maxResults}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!listResponse.ok) {
    const err = await listResponse.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gmail search failed (${listResponse.status})`);
  }

  const listData = await listResponse.json();
  if (!listData.messages || listData.messages.length === 0) {
    return [];
  }

  // Fetch each message's metadata + snippet in parallel (batch of 10 at a time)
  const messages = [];
  const chunks = [];
  for (let i = 0; i < listData.messages.length; i += 10) {
    chunks.push(listData.messages.slice(i, i + 10));
  }

  for (const chunk of chunks) {
    const fetched = await Promise.all(
      chunk.map(msg =>
        fetch(`${GMAIL_BASE}/messages/${msg.id}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date&metadataHeaders=Message-ID`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : null)
      )
    );

    for (const msg of fetched) {
      if (!msg) continue;
      const headers = {};
      (msg.payload?.headers || []).forEach(h => {
        headers[h.name.toLowerCase()] = h.value;
      });

      const fromEmail = extractEmail(headers.from || '');
      const toEmail = extractEmail(headers.to || '');
      const isOutbound = fromEmail !== email.toLowerCase();

      messages.push({
        id: msg.id,
        threadId: msg.threadId,
        messageIdHeader: headers['message-id'] || null,
        date: headers.date || '',
        timestamp: parseInt(msg.internalDate) || 0,
        from: headers.from || '',
        to: headers.to || '',
        subject: headers.subject || '(no subject)',
        snippet: msg.snippet || '',
        direction: isOutbound ? 'outbound' : 'inbound',
        labelIds: msg.labelIds || [],
      });
    }
  }

  // Sort newest first
  messages.sort((a, b) => b.timestamp - a.timestamp);
  return messages;
}

/**
 * Get the full body text of a specific message
 * @param {string} messageId - Gmail message ID
 * @returns {Promise<string>} Plain text body
 */
export async function getMessageBody(messageId) {
  const token = await getAccessToken();
  
  const response = await fetch(`${GMAIL_BASE}/messages/${messageId}?format=full`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch message (${response.status})`);
  }

  const msg = await response.json();
  return extractBody(msg.payload);
}

/**
 * Get full thread (all messages in a conversation)
 * @param {string} threadId - Gmail thread ID
 * @returns {Promise<Array>} Array of parsed messages in the thread
 */
export async function getThread(threadId) {
  const token = await getAccessToken();

  const response = await fetch(`${GMAIL_BASE}/threads/${threadId}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch thread (${response.status})`);
  }

  const thread = await response.json();
  return (thread.messages || []).map(msg => {
    const headers = {};
    (msg.payload?.headers || []).forEach(h => {
      headers[h.name.toLowerCase()] = h.value;
    });
    return {
      id: msg.id,
      threadId: msg.threadId,
      date: headers.date || '',
      timestamp: parseInt(msg.internalDate) || 0,
      from: headers.from || '',
      to: headers.to || '',
      subject: headers.subject || '(no subject)',
      snippet: msg.snippet || '',
      direction: null, // caller determines based on contact email
    };
  }).sort((a, b) => a.timestamp - b.timestamp); // oldest first in thread view
}

/**
 * General Gmail search — for finding any emails by query
 * @param {string} query - Gmail search query (same syntax as Gmail search bar)
 * @param {number} [maxResults=10]
 * @returns {Promise<Array>}
 */
export async function searchEmails(query, maxResults = 10) {
  const token = await getAccessToken();
  
  const encoded = encodeURIComponent(query);
  const response = await fetch(
    `${GMAIL_BASE}/messages?q=${encoded}&maxResults=${maxResults}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(`Gmail search failed (${response.status})`);
  }

  const data = await response.json();
  if (!data.messages || data.messages.length === 0) return [];

  // Fetch metadata for each
  const messages = await Promise.all(
    data.messages.map(msg =>
      fetch(`${GMAIL_BASE}/messages/${msg.id}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null)
    )
  );

  return messages.filter(Boolean).map(msg => {
    const headers = {};
    (msg.payload?.headers || []).forEach(h => {
      headers[h.name.toLowerCase()] = h.value;
    });
    return {
      id: msg.id,
      threadId: msg.threadId,
      date: headers.date || '',
      timestamp: parseInt(msg.internalDate) || 0,
      from: headers.from || '',
      to: headers.to || '',
      subject: headers.subject || '(no subject)',
      snippet: msg.snippet || '',
    };
  }).sort((a, b) => b.timestamp - a.timestamp);
}


// ============================================================
// FORMSPREE INBOX CHECK
// Searches Gmail for Formspree notification emails
// ============================================================

/**
 * Find Formspree submission notifications in Gmail
 * @param {number} [maxResults=20]
 * @returns {Promise<Array<{id, date, subject, snippet, from}>>}
 */
export async function getFormspreeEmails(maxResults = 20) {
  return searchEmails('from:noreply@formspree.io OR from:no-reply@formspree.io', maxResults);
}


// ============================================================
// HELPERS
// ============================================================

function extractEmail(headerValue) {
  const match = headerValue.match(/<([^>]+)>/);
  return (match ? match[1] : headerValue).toLowerCase().trim();
}

function extractBody(payload) {
  if (!payload) return '';

  // Direct body
  if (payload.body?.data) {
    return decodeBase64(payload.body.data);
  }

  // Multipart — find text/plain first, fall back to text/html
  if (payload.parts) {
    // Try text/plain first
    for (const part of payload.parts) {
      if (part.mimeType === 'text/plain' && part.body?.data) {
        return decodeBase64(part.body.data);
      }
    }
    // Fall back to text/html, strip tags
    for (const part of payload.parts) {
      if (part.mimeType === 'text/html' && part.body?.data) {
        const html = decodeBase64(part.body.data);
        return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
      }
    }
    // Recurse into nested multipart
    for (const part of payload.parts) {
      if (part.parts) {
        const found = extractBody(part);
        if (found) return found;
      }
    }
  }

  return '';
}

function decodeBase64(data) {
  try {
    return decodeURIComponent(escape(atob(data.replace(/-/g, '+').replace(/_/g, '/'))));
  } catch {
    try {
      return atob(data.replace(/-/g, '+').replace(/_/g, '/'));
    } catch {
      return '';
    }
  }
}
